import { useAppDispatch, useAppSelector } from 'app/hooks';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import service, { DeliveryCheckStatusResponse } from 'components/common/StatusHeader/services';
import { selectJobInfo } from 'features/Auth/authSlice';
import { selectWorkerStatus } from 'features/common/workerSlice';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Col, ModalProps, Row } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { DATE_HYPHEN_FORMAT } from 'utils';
import AsyncDeliveryStatusChecker from '../components/AsyncDeliveryStatusChecker';
import DeliveryDetailModal from '../components/DeliveryDetailModal';
import DeliverySettingsModal from '../components/DeliverySettingsModal';
import SearchDelivery from '../components/SearchDelivery';
import SearchDeliveryResult from '../components/SearchDeliveryResult';
import TestReflectionModal from '../components/TestReflectionModal';
import { APPROVAL_STATUS_ALL, DELIVERY_STATUS_ALL, STATUS_RESPONSE_PROGRESSING } from '../constants';
import {
  deliveryListActions,
  selectDeliveriesData,
  selectDeliveryApprovalStatus,
  selectDeliveryDeliveryStatus,
  selectDeliveryWorkflowActions,
} from '../deliverySlice';
import { Delivery, DeliverySearchPayload } from '../models/delivery';
import './DeliveryPage.scss';

function DeliveryPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();

  const deliverySettingModalRef = useRef<ModalProps>(DeliverySettingsModal);
  const testReflectionModalRef = useRef<ModalProps>(TestReflectionModal);

  const selectedJob = useAppSelector(selectJobInfo);

  // selector
  const deliveryStatusList = useAppSelector(selectDeliveryDeliveryStatus);
  const approvalStatusList = useAppSelector(selectDeliveryApprovalStatus);
  const deliveryResultList = useAppSelector(selectDeliveriesData);
  const workflowActions = useAppSelector(selectDeliveryWorkflowActions);
  const workerStatus = useAppSelector(selectWorkerStatus);

  // state
  const [showDeliveryDetailModal, setShowDeliveryDetailModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<number[]>([]);

  // filter search
  const [filterSearch, setFilterSearch] = useState<DeliverySearchPayload>({
    start_date: moment().subtract(1, 'weeks').format(DATE_HYPHEN_FORMAT),
    end_date: moment().add(1, 'weeks').format(DATE_HYPHEN_FORMAT),
    approval_status: APPROVAL_STATUS_ALL,
    delivery_status: Number(DELIVERY_STATUS_ALL),
  });

  // load data when page init
  useEffect(() => {
    dispatch(deliveryListActions.fetchDeliveriesInfo(filterSearch));
    service.checkApprovedDelivery().then((value: DeliveryCheckStatusResponse) => {
      dispatch(
        deliveryListActions.updateIsLinkDisabled(
          value.data.delivery_count === value.data.delivery_count_approved ? false : true
        )
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSearch]);

  useEffect(() => {
    const fetchWorkflowActions = async () => {
      if (selectedDeliveryIds.length === 1) {
        const deliveryId = selectedDeliveryIds[0];
        dispatch(deliveryListActions.fetchWorkflowActions(deliveryId));
      }
    };

    fetchWorkflowActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeliveryIds]);

  const handleSelectDelivery = (deliveryId: number) => {
    setSelectedDeliveryIds([deliveryId]);
  };

  const handleDeselectDelivery = (deliveryId: number) => {
    setSelectedDeliveryIds([]);
  };

  useEffect(() => {
    dispatch(deliveryListActions.fetchDeliveryMasterData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDisplayDeliverySettingsModal = () => {
    deliverySettingModalRef.current.openModal();
  };

  const handleDisplayTestReflectionModal = () => {
    testReflectionModalRef.current.openModal();
  };

  const handleDisplayDeliveryDetailModal = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setShowDeliveryDetailModal(true);
  };

  const handleCreateDeliverySettingsSuccess = () => {
    deliverySettingModalRef.current.closeModal();
    //reload data
    dispatch(deliveryListActions.fetchDeliveriesInfo(filterSearch));
    // dispatch(workerActions.resetWorker());
    service.checkApprovedDelivery().then((value: DeliveryCheckStatusResponse) => {
      dispatch(
        deliveryListActions.updateIsLinkDisabled(
          value.data.delivery_count === value.data.delivery_count_approved ? false : true
        )
      );
    });
  };

  const handleCreateTestReflectionSuccess = () => {
    testReflectionModalRef.current.closeModal();
    //reload data
    dispatch(deliveryListActions.fetchDeliveriesInfo(filterSearch));
  };

  if (deliveryStatusList.length === 0 && approvalStatusList.length === 0) {
    return <></>;
  }

  const performWorkflowAction = (deliveryId: number, action: string) => {
    let employeeJobId = selectedJob?.employee_job_id || 0;
    dispatch(deliveryListActions.performWorkflowAction({ deliveryId, action, filterSearch, employeeJobId }));
    service.checkApprovedDelivery().then((value: DeliveryCheckStatusResponse) => {
      dispatch(
        deliveryListActions.updateIsLinkDisabled(
          value.data.delivery_count === value.data.delivery_count_approved ? false : true
        )
      );
    });
  };

  function handleSearchSumit(payload: DeliverySearchPayload) {
    setFilterSearch(payload);
  }

  const handleReloadDeliveries = () => {
    dispatch(deliveryListActions.fetchDeliveriesInfo(filterSearch));
  };

  return (
    <>
      {/* Async Delivery Status Checker - monitors ongoing async delivery creation */}
      <AsyncDeliveryStatusChecker onDeliveryCompleted={handleReloadDeliveries} />

      <TestReflectionModal onCreateSuccess={handleCreateTestReflectionSuccess} ref={testReflectionModalRef} />
      <DeliverySettingsModal onCreateSuccess={handleCreateDeliverySettingsSuccess} ref={deliverySettingModalRef} />
      {showDeliveryDetailModal && (
        <DeliveryDetailModal delivery={selectedDelivery} onClose={() => setShowDeliveryDetailModal(false)} />
      )}
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader onAction={() => history.push('/')} />
        </div>
        <div className="page-main">
          <div className="page-content d-flex flex-column">
            <div className="row">
              <div className="col mb-3 d-flex align-items-center">
                <FeatureTitleBar title={t('DeliveryFeature.Text.distribution_setting_status_list')}></FeatureTitleBar>
                <button
                  type="button"
                  style={{
                    marginLeft: '10px',
                    color: 'white',
                    height: '30px',
                    backgroundColor: '#E67300',
                    border: 'none',
                    borderRadius: '0px',
                  }}
                  className="btn"
                  onClick={handleDisplayTestReflectionModal}
                >
                  {t('DeliveryFeature.Component.test_reflection')}
                </button>
              </div>
            </div>
            <SearchDelivery onSubmit={handleSearchSumit} />

            <Row className="mb-2">
              <Col className="d-flex justify-content-start">
                <button
                  type="button"
                  onClick={handleDisplayDeliverySettingsModal}
                  className="btn btn-primary"
                  disabled={workerStatus === STATUS_RESPONSE_PROGRESSING}
                >
                  {t('DeliveryFeature.Text.new_distribution_setting')}
                </button>
              </Col>
              <Col
                className="d-flex justify-content-end"
                style={{ display: workerStatus === STATUS_RESPONSE_PROGRESSING ? 'none' : undefined }}
              >
                {selectedDeliveryIds.length === 1 && (
                  <>
                    {workflowActions.includes('CANCEL_APPLY') && (
                      <button
                        type="button"
                        className="btn btn--yellow ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'CANCEL_APPLY')}
                      >
                        {t('DeliveryFeature.Component.cancel_apply')}
                      </button>
                    )}
                    {workflowActions.includes('APPLY') && (
                      <button
                        type="button"
                        className="btn btn--green ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'APPLY')}
                      >
                        {t('DeliveryFeature.Component.apply')}
                      </button>
                    )}
                    {workflowActions.includes('CANCEL_CONFIRM') && (
                      <button
                        type="button"
                        className="btn btn--yellow ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'CANCEL_CONFIRM')}
                      >
                        {t('DeliveryFeature.Component.cancel_confirm')}
                      </button>
                    )}

                    {workflowActions.includes('CONFIRM') && (
                      <button
                        type="button"
                        className="btn btn--green ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'CONFIRM')}
                      >
                        {t('DeliveryFeature.Component.confirm')}
                      </button>
                    )}
                    {workflowActions.includes('APPROVE') && (
                      <button
                        type="button"
                        className="btn btn--green ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'APPROVE')}
                      >
                        {t('DeliveryFeature.Component.approve')}
                      </button>
                    )}

                    {workflowActions.includes('SEND_BACK') && (
                      <button
                        type="button"
                        className="btn btn--violet ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'SEND_BACK')}
                      >
                        {t('DeliveryFeature.Component.send_back')}
                      </button>
                    )}

                    {/* {workflowActions.includes('CANCEL_APPROVE') && (
                      <button
                        type="button"
                        className="btn btn--yellow ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'CANCEL_APPROVE')}
                      >
                        {t('DeliveryFeature.Component.cancel_approve')}
                      </button>
                    )} */}
                    {workflowActions.includes('REJECT_APPROVE') && (
                      <button
                        type="button"
                        className="btn btn--violet ms-2"
                        disabled={workerStatus === STATUS_RESPONSE_PROGRESSING}
                        onClick={() => {
                          showGlobalModal(() => (
                            <ConfirmModal
                              title={t('App.confirm')}
                              message={t('DeliveryFeature.Confirm.reject_approve') || '承認を拒否しますか？'}
                              onConfirm={() => {
                                performWorkflowAction(selectedDeliveryIds[0], 'REJECT_APPROVE');
                              }}
                            />
                          ));
                        }}
                      >
                        {t('DeliveryFeature.Component.reject')}
                      </button>
                    )}
                    {workflowActions.includes('REJECT_CONFIRM') && (
                      <button
                        type="button"
                        className="btn btn--violet ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'REJECT_CONFIRM')}
                      >
                        {t('DeliveryFeature.Component.reject_confirm')}
                      </button>
                    )}
                    {workflowActions.includes('DELETE') && (
                      <button
                        type="button"
                        className="btn btn--red ms-2"
                        onClick={() => performWorkflowAction(selectedDeliveryIds[0], 'DELETE')}
                      >
                        {t('DeliveryFeature.Component.delete')}
                      </button>
                    )}
                  </>
                )}
              </Col>
            </Row>
            {deliveryResultList.length > 0 && (
              <SearchDeliveryResult
                handleDisplayDeliveryDetailModal={handleDisplayDeliveryDetailModal}
                handleSelectDelivery={handleSelectDelivery}
                handleDeselectDelivery={handleDeselectDelivery}
                data={deliveryResultList}
                selectedDeliveryIds={selectedDeliveryIds}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
export default DeliveryPage;
